// Admin authentication utilities

const API_BASE = import.meta.env.VITE_API_BASE || '';
const AUTH_TOKEN_KEY = 'xavi_admin_token';
const AUTH_EXPIRES_KEY = 'xavi_admin_expires';

export interface LoginResponse {
  token: string;
  expiresAt: number;
}

export async function login(username: string, password: string): Promise<LoginResponse> {
  const response = await fetch(`${API_BASE}/api/admin/login`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ username, password }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Login failed' }));
    throw new Error(error.error || 'Login failed');
  }

  const data: LoginResponse = await response.json();

  // Store token
  localStorage.setItem(AUTH_TOKEN_KEY, data.token);
  localStorage.setItem(AUTH_EXPIRES_KEY, data.expiresAt.toString());

  return data;
}

export function logout(): void {
  localStorage.removeItem(AUTH_TOKEN_KEY);
  localStorage.removeItem(AUTH_EXPIRES_KEY);
}

export function getToken(): string | null {
  const token = localStorage.getItem(AUTH_TOKEN_KEY);
  const expiresAt = localStorage.getItem(AUTH_EXPIRES_KEY);

  if (!token || !expiresAt) {
    return null;
  }

  // Check if expired
  if (Date.now() > parseInt(expiresAt, 10)) {
    logout();
    return null;
  }

  return token;
}

export function isAuthenticated(): boolean {
  return getToken() !== null;
}

// Authenticated fetch wrapper
export async function authFetch(url: string, options: RequestInit = {}): Promise<Response> {
  const token = getToken();

  if (!token) {
    throw new Error('Not authenticated');
  }

  const headers = new Headers(options.headers);
  headers.set('Authorization', `Bearer ${token}`);
  headers.set('Content-Type', 'application/json');

  return fetch(`${API_BASE}${url}`, {
    ...options,
    headers,
  });
}

// Helper for authenticated API calls
export async function authApiCall<T>(
  url: string,
  options: RequestInit = {}
): Promise<T> {
  const response = await authFetch(url, options);

  if (response.status === 401) {
    logout();
    throw new Error('Session expired. Please login again.');
  }

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Request failed' }));
    throw new Error(error.error || error.message || `HTTP ${response.status}`);
  }

  // Handle 204 No Content
  if (response.status === 204) {
    return {} as T;
  }

  return response.json();
}
